import { cva, type VariantProps } from "class-variance-authority";
import { Loader2, type LucideIcon } from "lucide-react";
import * as React from "react";
import { cn } from "../../lib/cn";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl text-xs font-bold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gv-brand)] focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 cursor-pointer",
  {
    variants: {
      variant: {
        primary:
          "bg-[var(--gv-brand)] text-white hover:bg-[var(--gv-brand-dark)] shadow-xs",
        secondary:
          "bg-[var(--gv-brand-soft)] text-[var(--gv-brand)] hover:bg-[var(--gv-brand-muted)]",
        outline:
          "border border-[var(--gv-border)] bg-white text-[var(--gv-ink)] hover:bg-[var(--gv-surface)]",
        ghost: "text-[var(--gv-ink-muted)] hover:bg-[var(--gv-brand-soft)] hover:text-[var(--gv-ink)]",
        danger: "bg-rose-600 text-white hover:bg-rose-700 shadow-xs",
      },
      size: {
        sm: "h-8 px-3",
        md: "h-9 px-4",
        lg: "h-10 px-5 text-sm",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  loading?: boolean;
  leftIcon?: LucideIcon;
  rightIcon?: LucideIcon;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      loading = false,
      leftIcon: LeftIcon,
      rightIcon: RightIcon,
      children,
      disabled,
      ...props
    },
    ref
  ) => {
    return (
      <button
        ref={ref}
        className={cn(buttonVariants({ variant, size }), className)}
        disabled={disabled || loading}
        {...props}
      >
        {loading ? (
          <Loader2 className="animate-spin" />
        ) : (
          LeftIcon && <LeftIcon />
        )}
        {children}
        {!loading && RightIcon && <RightIcon />}
      </button>
    );
  }
);

Button.displayName = "Button";

export { buttonVariants };
